import React from "react";

export function HelpTab() {
  // Detect iOS and if already installed
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true;
  const showInstallInstructions = isIOS && !isStandalone;

  return (
    <div className="settings-section">
      {/* iOS Install Instructions - only show on iOS when not installed */}
      {showInstallInstructions && (
        <>
          <div className="settings-group-header">Install BikeDash</div>
          <div className="help-section">
            <h3 className="help-section-title">Add to Home Screen</h3>
            <div className="help-content">
              <p>
                Get the full app experience by installing BikeDash to your home
                screen:
              </p>
              <ol
                style={{
                  paddingLeft: "var(--spacing-xl)",
                  margin: "var(--spacing-md) 0",
                }}
              >
                <li
                  style={{
                    marginBottom: "var(--spacing-md)",
                    display: "flex",
                    alignItems: "start",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Tap the Share button</span>
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 15 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <path d="M7.5 1v11M7.5 1l-3 3M7.5 1l3 3" />
                      <path d="M1.5 9v7.5c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2h-2" />
                      <path d="M5.5 7h-2c-1.1 0-2 .9-2 2" />
                    </svg>
                    <span>in Safari</span>
                  </div>
                </li>
                <li
                  style={{
                    marginBottom: "var(--spacing-md)",
                    display: "flex",
                    alignItems: "start",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>Scroll down and tap "Add to Home Screen"</span>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <rect x="3" y="3" width="18" height="18" rx="4" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  </div>
                </li>
                <li style={{ marginBottom: "var(--spacing-sm)" }}>
                  Tap "Add" to install
                </li>
              </ol>
              <p
                style={{
                  fontSize: "12px",
                  color: "var(--text-secondary)",
                  marginTop: "var(--spacing-md)",
                }}
              >
                <strong>Benefits:</strong> Faster loading, works offline,
                full-screen experience, and no browser UI.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Quick Start */}
      <div className="settings-group-header">Quick Start</div>

      <div className="help-section">
        <h3 className="help-section-title">Your First Ride</h3>
        <div className="help-content">
          <p>
            Start tracking your ride in seconds using the activity controls:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Activity controls (Start/Pause/Stop)
          </div>
          <ul>
            <li>
              <strong>Start:</strong> Begin recording GPS, speed, and distance
            </li>
            <li>
              <strong>Pause:</strong> Take a break (or enable auto-pause in
              settings)
            </li>
            <li>
              <strong>Stop:</strong> End and save your activity
            </li>
          </ul>
          <p>
            Your ride data is saved locally and syncs automatically when online.
          </p>
        </div>
      </div>

      {/* Routes & Navigation */}
      <div className="settings-group-header">Routes & Navigation</div>

      <div className="help-section">
        <h3 className="help-section-title">Finding Bike Routes & Trails</h3>
        <div className="help-content">
          <p>
            Use the search bar to discover bike routes and trails. Results are
            filtered to prioritize cycling-friendly paths:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Search bar and results
          </div>
          <ul>
            <li>
              <strong>Local Search:</strong> Find nearby trails by name
            </li>
            <li>
              <strong>Global Search:</strong> Search anywhere by address or
              landmark
            </li>
            <li>
              <strong>Saved Routes:</strong> Search your personal route
              collection by name
            </li>
          </ul>
        </div>
      </div>

      <div className="help-section">
        <h3 className="help-section-title">Following a Route</h3>
        <div className="help-content">
          <p>Navigate along bike trails using the highlighted route overlay:</p>
          <div className="help-image-placeholder">
            📸 Image: Activated route with highlighted trail
          </div>
          <ol
            style={{
              paddingLeft: "var(--spacing-xl)",
              margin: "var(--spacing-md) 0",
            }}
          >
            <li style={{ marginBottom: "var(--spacing-sm)" }}>
              <strong>Click a search result</strong> to zoom to the route
            </li>
            <li style={{ marginBottom: "var(--spacing-sm)" }}>
              <strong>Activate the route</strong> to highlight the trail in
              green
            </li>
            <li style={{ marginBottom: "var(--spacing-sm)" }}>
              Follow the highlighted path as you ride
            </li>
          </ol>
          <p>
            <strong>Tip:</strong> Long-press anywhere on the map to create a
            route to that location.
          </p>
          <p
            style={{
              marginTop: "var(--spacing-md)",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            Active routes appear in green, your current track in blue.
          </p>
        </div>
      </div>

      <div className="help-section">
        <h3 className="help-section-title">Saving Routes</h3>
        <div className="help-content">
          <p>Build your personal library of favorite routes:</p>
          <div className="help-image-placeholder">
            📸 Image: Saving a route with custom name
          </div>
          <ul>
            <li>
              <strong>From search results:</strong> Save any discovered route
              with a custom name
            </li>
            <li>
              <strong>From activities:</strong> Convert past rides into reusable
              routes
            </li>
            <li>
              <strong>Find later:</strong> Search by name to quickly reload
              saved routes
            </li>
          </ul>
          <p>
            Perfect for repeating favorite trails or sharing routes with
            friends.
          </p>
        </div>
      </div>

      {/* Your Activities */}
      <div className="settings-group-header">Your Activities</div>

      <div className="help-section">
        <h3 className="help-section-title">Managing Your Rides</h3>
        <div className="help-content">
          <p>
            Access all recorded activities from the user menu →{" "}
            <strong>Activities</strong>:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Activities list with statistics
          </div>
          <ul>
            <li>
              View complete ride history with stats (distance, speed, time)
            </li>
            <li>Replay rides on the map</li>
            <li>Convert rides to routes for future use</li>
            <li>Export to GPX format</li>
            <li>Delete activities you no longer need</li>
          </ul>
        </div>
      </div>

      {/* Customization */}
      <div className="settings-group-header">Customization</div>

      <div className="help-section">
        <h3 className="help-section-title">Map Styles</h3>
        <div className="help-content">
          <p>
            Choose from various map styles in the <strong>Map</strong> settings
            tab:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Map style selector
          </div>
          <ul>
            <li>
              <strong>Open Maps:</strong> Free OpenStreetMap, ESRI, and CartoDB
              styles
            </li>
            <li>
              <strong>Thunderforest:</strong> Premium cycling and outdoor maps
              (login required)
            </li>
            <li>
              <strong>Satellite Overlay:</strong> Blend satellite imagery with
              any map
            </li>
          </ul>
          <p>
            <strong>Tip:</strong> Star your favorites for quick access during
            rides.
          </p>
        </div>
      </div>

      <div className="help-section">
        <h3 className="help-section-title">Track & Route Styling</h3>
        <div className="help-content">
          <p>
            Customize how tracks and routes appear in <strong>Map</strong>{" "}
            settings:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Track style controls
          </div>
          <ul>
            <li>
              <strong>Width:</strong> Line thickness (1-15pt, auto-scales with
              zoom)
            </li>
            <li>
              <strong>Color:</strong> Preset or custom colors
            </li>
            <li>
              <strong>Outline:</strong> Add contrast for visibility on any map
            </li>
            <li>
              <strong>Auto Outline:</strong> Black or white based on line color
              brightness
            </li>
          </ul>
          <p>Activity tracks and route tracks can be styled independently.</p>
        </div>
      </div>

      <div className="help-section">
        <h3 className="help-section-title">Recording Settings</h3>
        <div className="help-content">
          <p>
            Configure activity recording in the <strong>Activity</strong> tab:
          </p>
          <div className="help-image-placeholder">
            📸 Image: Activity settings
          </div>
          <ul>
            <li>
              <strong>Auto-Pause:</strong> Pause automatically when stopped
            </li>
            <li>
              <strong>Auto-Save Interval:</strong> How often to save progress
              (15-120s)
            </li>
            <li>
              <strong>Units:</strong> Metric or imperial
            </li>
            <li>
              <strong>Privacy:</strong> Default visibility for activities
            </li>
          </ul>
        </div>
      </div>

      {/* Advanced Features */}
      <div className="settings-group-header">Advanced Features</div>

      <div className="help-section">
        <h3 className="help-section-title">Offline Mode</h3>
        <div className="help-content">
          <p>
            BikeDash works without internet! Activities save locally and sync
            automatically when reconnected.
          </p>
          <ul>
            <li>Map tiles cache automatically as you explore</li>
            <li>Record activities anywhere, anytime</li>
            <li>Enable full offline mode in settings</li>
          </ul>
        </div>
      </div>

      <div className="help-section">
        <h3 className="help-section-title">Performance & Battery</h3>
        <div className="help-content">
          <ul>
            <li>
              <strong>Keep Screen On:</strong> Prevent sleep during rides (in
              settings)
            </li>
            <li>
              <strong>Max Update Rate:</strong> Lower FPS = longer battery life
              (1-60fps)
            </li>
            <li>
              <strong>Smooth GPS Motion:</strong> Animated vs instant position
              updates
            </li>
            <li>
              <strong>Install as App:</strong> Add to home screen for native
              experience
            </li>
          </ul>
        </div>
      </div>

      {/* Support */}
      <div className="settings-group-header">Need More Help?</div>

      <div className="help-section">
        <div className="help-content">
          <p>
            Questions or issues? Reach out to support or check the online
            documentation.
          </p>
          <p
            style={{
              marginTop: "var(--spacing-md)",
              color: "var(--text-secondary)",
              fontSize: "12px",
            }}
          >
            <strong>Environment:</strong> {window.location.hostname}
          </p>
        </div>
      </div>
    </div>
  );
}
